import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle } from 'lucide-react';

import { useDegradedIngresses } from '@/hooks/use-degraded-ingresses';

interface DegradedIngressBannerProps {
  serverName: string;
}

/**
 * Banner shown on the server-detail page when the selector is currently
 * excluding *this* server from being handed out (all downstream EXIT_NODEs
 * stale; see `workers/fleet-exit-health-propagate.service.ts`).
 *
 * Renders nothing when the server is healthy — keeps the page clean
 * during the common case. Reason + duration come straight from the
 * Redis marker so they survive backend restarts.
 */
export function DegradedIngressBanner({ serverName }: DegradedIngressBannerProps): JSX.Element | null {
  const query = useDegradedIngresses();
  const entry = query.data?.find((d) => d.name === serverName);
  if (!entry) return null;

  const since = new Date(entry.since);

  return (
    <div
      className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-950/30"
      role="alert"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
      <div className="text-sm">
        <p className="font-medium text-amber-900 dark:text-amber-200">
          Excluded from the runtime selector
        </p>
        <p className="mt-0.5 text-xs text-amber-900/80 dark:text-amber-200/80">
          Reason: <span className="font-mono">{entry.reason}</span>. The selector has been
          skipping this server for{' '}
          {formatDistanceToNow(since, { addSuffix: false })}. It will return to the pool
          automatically when its downstream exit heartbeats again.
        </p>
      </div>
    </div>
  );
}
